import { ROOM_KEYS } from "../Components/PassengersData/PassengersData";
import { getRoomsSetByCount } from "./LDSHelpers";
import { ldsOurTotalFareDisplayNis } from "./ldsVacationPackageDisplayPrice";
import {
  readOrganizedTourDealOurTotalFare,
  stampOrganizedTourDealHrOnFareDetails,
} from "../modules/organizedTours/utils/applyOrganizedTourDealHrToRoomFare";
import { resolvePublicRoomPricingSnapshot } from "./publicRoomPricingSSOT";
import {
  getFinalIlsFromOurTotalFareForDisplay,
  formatRoomIlsUsdDisplay,
} from "./packages/computeRoomDisplayPrice";
import {
  isVacationPackagesFlightsHotelAgentDeal,
  vacationPackageGlobalAgentMarkupIls,
} from "./vacationPackageRoomAgentMarkup";

function round2(n) {
  return Math.round(Number(n) * 100) / 100;
}

function num(v) {
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
}

function pickRoomId(room) {
  if (!room || typeof room !== "object") return null;
  return (
    room["@seqNum"] ??
    room[ROOM_KEYS.SEQ_NUM] ??
    room.roomId ??
    room.RoomId ??
    null
  );
}

export function normalizeRoomTypeLabel(roomOrSuggestion) {
  const t =
    roomOrSuggestion?.roomType ??
    roomOrSuggestion?.RoomType ??
    roomOrSuggestion?.roomTypeName ??
    roomOrSuggestion?.RoomTypeName ??
    roomOrSuggestion?.roomName ??
    roomOrSuggestion?.RoomName ??
    "";
  const s = String(t).trim();
  return s ? s.toUpperCase() : null;
}

export function resolveSlotOccupancy(slot) {
  if (!slot || typeof slot !== "object") return null;
  return {
    adults: Array.isArray(slot.adults) ? slot.adults.length : Number(slot.adults) || 0,
    kids: Array.isArray(slot.kids) ? slot.kids.length : Number(slot.kids ?? slot.children) || 0,
    infants: Array.isArray(slot.infants) ? slot.infants.length : Number(slot.infants) || 0,
    roomSet: slot.room?.[ROOM_KEYS.ROOM_SET] ?? slot.room?.roomSet ?? null,
  };
}

export function occupanciesMatch(a, b) {
  if (!a || !b) return false;
  return (
    a.adults === b.adults &&
    a.kids === b.kids &&
    (a.infants || 0) === (b.infants || 0)
  );
}

export function occupancySignature(occupancy) {
  if (!occupancy) return null;
  return `${occupancy.adults ?? 0}a${occupancy.kids ?? 0}c${occupancy.infants || 0}i`;
}

export function pickDealSeq(row) {
  if (!row || typeof row !== "object") return null;
  const seq = row["@seqNum"] ?? row.seqNum ?? row.dealSeq ?? row.roomId ?? row.RoomId;
  if (seq == null || String(seq).trim() === "") return null;
  return String(seq).trim();
}

export function readEngineBasePriceWithCommissionIls(ourFareDetails) {
  const tf = ourFareDetails?.ourTotalFare;
  if (!tf || typeof tf !== "object") return null;
  const pre = num(tf.pricePreFinalRound);
  if (pre != null && pre > 0) return round2(pre);
  const initial = num(tf.initialPrice);
  const hr = num(tf.hrCommission);
  if (initial != null && initial > 0 && hr != null && hr >= 0) {
    return round2(initial + hr);
  }
  const pwc = num(tf.priceWithCommission);
  if (pwc != null && pwc > 0) return round2(pwc);
  return null;
}

export function readRoomPricingSnapshot(slot) {
  const snap = slot?.pricingSnapshot ?? slot?.room?.pricingSnapshot ?? null;
  if (!snap || typeof snap !== "object") return null;
  const finalPriceIls = num(snap.finalPriceIls ?? snap.publicFinalPrice);
  if (snap.status === "ready" && finalPriceIls != null && finalPriceIls > 0) {
    return snap;
  }
  return null;
}

export function resolveAgentRoomDisplayedPriceFromFare(ourFareDetails) {
  const v = num(ourFareDetails?.ourTotalFare?.priceWithCommissionAndCreditCardFee);
  return v != null && v > 0 ? v : null;
}

function mapPublicSnapshotToNormalized(publicSnap, displayFmt, extras = {}) {
  const finalPriceIls = num(publicSnap?.publicFinalPrice);
  const base =
    num(extras.basePriceWithCommissionIls) ??
    num(publicSnap?.basePriceWithCommissionIls) ??
    num(publicSnap?.agentFinalPrice);
  return {
    ...publicSnap,
    status: publicSnap?.status ?? "ready",
    finalPriceIls,
    publicFinalPrice: finalPriceIls,
    agentFinalPrice: num(publicSnap?.agentFinalPrice),
    basePriceWithCommissionIls: base,
    dealSeq: extras.dealSeq ?? publicSnap?.dealSeq ?? null,
    occupancySignature: extras.occupancySignature ?? publicSnap?.occupancySignature ?? null,
    supplierGross: extras.supplierGross ?? publicSnap?.supplierGross ?? null,
    currency: extras.currency ?? publicSnap?.currency ?? "ILS",
    fxRate: extras.fxRate ?? publicSnap?.fxRate ?? null,
    via: extras.via ?? publicSnap?.via ?? null,
    ilsDisplayRounded: displayFmt?.ilsDisplayRounded ?? (finalPriceIls != null ? Math.round(finalPriceIls) : null),
    usdDisplayIntegerStr: displayFmt?.usdDisplayIntegerStr ?? null,
    usdRaw: displayFmt?.usdRaw ?? null,
    source: publicSnap?.source ?? "selected_public_room_price_snapshot",
    productType: publicSnap?.productType ?? "vacation_package_room",
  };
}

function mapAgentSnapshotToNormalized({ displayIls, ourFareDetails, displayFmt, roomSuggestion, occupancy, via = null }) {
  const tf = ourFareDetails?.ourTotalFare;
  const base = readEngineBasePriceWithCommissionIls(ourFareDetails);
  return {
    status: "ready",
    productType: "vacation_package_room",
    finalPriceIls: displayIls,
    agentFinalPrice: displayIls,
    publicFinalPrice: null,
    basePriceWithCommissionIls: base,
    dealSeq: pickDealSeq(roomSuggestion) ?? pickDealSeq(occupancy),
    occupancySignature: occupancySignature(occupancy),
    supplierGross: num(roomSuggestion?.totalFare) ?? num(tf?.netPrice),
    currency: "ILS",
    fxRate: num(tf?.fx_rate) ?? num(tf?.fxRate),
    via: via ?? "extended_row_ssot",
    ilsDisplayRounded: displayFmt?.ilsDisplayRounded ?? Math.round(displayIls),
    usdDisplayIntegerStr: displayFmt?.usdDisplayIntegerStr ?? null,
    usdRaw: displayFmt?.usdRaw ?? null,
    roomId: pickRoomId(roomSuggestion),
    roomType: roomSuggestion?.roomType ?? roomSuggestion?.RoomType ?? occupancy?.roomType ?? null,
    roomSet: roomSuggestion?.roomSet ?? occupancy?.roomSet ?? null,
    originalCurrency: tf?.originalCurrency ?? null,
    finalPriceGBP: tf?.finalPriceGBP ?? null,
    source: "agent_room_ourFareDetails_ssot",
    sourcePriceField: "ourTotalFare.priceWithCommission",
    fallbackUsed: false,
  };
}

function getFirstFare(adultFare, childFare, numberOfKids, kidsAges) {
  let childFareFR = 0;
  if (Array.isArray(childFare)) {
    for (let i = 0; i < childFare.length; i++) {
      let j = 0;
      for (j = 0; j < kidsAges.length; j++) {
        const age = kidsAges[j];
        if (age >= childFare[i].ageFrom && age <= childFare[i].ageTo) {
          childFareFR += Number(childFare[i].fare);
          break;
        }
      }
      if (j === kidsAges.length) childFareFR += Number(adultFare);
    }
  } else if (!childFare || !childFare.fare) {
    childFareFR = numberOfKids * adultFare;
  } else {
    for (let j = 0; j < kidsAges.length; j++) {
      const age = kidsAges[j];
      if (age >= childFare.ageFrom && age <= childFare.ageTo) {
        childFareFR += Number(childFare.fare);
      } else childFareFR += Number(adultFare);
    }
  }
  return childFareFR;
}

function buildFeeCtx({
  selectedResult,
  handleFeeData,
  numberOfRooms,
  getCreditCardFeePercent,
}) {
  const vpAgentMarkupCards = isVacationPackagesFlightsHotelAgentDeal(selectedResult);
  const globalMarkupIlsPreview =
    vpAgentMarkupCards && handleFeeData?.type === "+"
      ? vacationPackageGlobalAgentMarkupIls(handleFeeData)
      : null;
  return {
    handleFeeData,
    numberOfRooms,
    getCreditCardFeePercent,
    vacationPackagesPerRoomFixedMarkup:
      vpAgentMarkupCards && handleFeeData?.type === "+",
    perRoomFixedMarkupIls:
      vpAgentMarkupCards && handleFeeData?.type === "+"
        ? globalMarkupIlsPreview
        : null,
  };
}

function computeOurFareForSuggestion({
  roomSuggestion,
  occupancy,
  selectedResult,
  calculateLDSDealFare,
}) {
  const {
    childFare = { fare: 0 },
    adultFare,
    infantFare = 0,
    totalFare = 0,
  } = roomSuggestion || {};
  const { fareDetails, dealType, hotel, tour, supplierDetails } = selectedResult || {};

  let calculatedTotalFare = totalFare;
  if (!calculatedTotalFare) {
    const kidsAges = Array(occupancy.kids).fill(10);
    const rawSum =
      Number(adultFare) * occupancy.adults +
      Number(getFirstFare(adultFare, childFare, occupancy.kids, kidsAges)) +
      Number(infantFare) * (occupancy.infants || 0);
    calculatedTotalFare = round2(rawSum);
  } else if (occupancy.infants > 0 && infantFare) {
    calculatedTotalFare = Number(calculatedTotalFare) + Number(infantFare) * occupancy.infants;
  }

  const fare = calculateLDSDealFare({
    dealFare: {
      adultFare,
      totalFare: calculatedTotalFare,
      childFareExt: childFare,
      infantFare,
    },
    fareDetails,
    dealType,
    hotel,
    tour,
    supplierDetails,
  });
  if (String(dealType ?? "") === "OrganizedTour") {
    stampOrganizedTourDealHrOnFareDetails(
      fare,
      readOrganizedTourDealOurTotalFare(selectedResult)
    );
  }
  return fare;
}

/**
 * Pick extended-deal row for add-room.
 * Requires an explicit seq / room type when several types share the occupancy.
 * Does not inherit Room 0's room type.
 */
export function findExtendedDealRowForAddRoomOccupancy({
  occupancy,
  extendedDealApiData,
}) {
  if (!occupancy || !Array.isArray(extendedDealApiData)) return null;

  const baseRoomSets = getRoomsSetByCount(occupancy.adults, occupancy.kids);
  const neededRoomSets = occupancy.roomSet
    ? [occupancy.roomSet, ...baseRoomSets]
    : baseRoomSets;

  const candidates = extendedDealApiData.filter((row) => {
    if (!neededRoomSets.includes(row?.roomSet)) return false;
    if (occupancy.infants > 0 && !row?.infantFare) return false;
    return true;
  });

  if (candidates.length === 0) return null;

  const preferredSeq =
    occupancy.dealSeq ?? occupancy["@seqNum"] ?? occupancy.seqNum ?? occupancy.roomId;
  if (preferredSeq != null && String(preferredSeq).trim() !== "") {
    const seqKey = String(preferredSeq).trim();
    const bySeq = candidates.find((row) => pickDealSeq(row) === seqKey);
    if (bySeq) return bySeq;
  }

  const preferredType = normalizeRoomTypeLabel({
    roomType: occupancy.roomType ?? occupancy.RoomType,
  });
  if (preferredType) {
    const sameType = candidates.filter(
      (row) => normalizeRoomTypeLabel(row) === preferredType
    );
    if (sameType.length === 1) return sameType[0];
    if (sameType.length > 1) return sameType[0];
  }

  if (candidates.length === 1) return candidates[0];

  const distinctTypes = new Set(
    candidates.map((row) => normalizeRoomTypeLabel(row)).filter(Boolean)
  );
  if (distinctTypes.size > 1) return null;

  return candidates[0];
}

export function buildVacationPackageRoomPricingSnapshot({
  roomSuggestion,
  ourFareDetails,
  occupancy = null,
  selectedRoomId = null,
  publicSitePricingSettings = null,
  agentHotelPricing = null,
  selectedResult = null,
  handleFeeData = null,
  getCreditCardFeePercent = null,
  numberOfRooms = 1,
  ilsToUsd = null,
  board = null,
  packageId = null,
  hotelId = null,
}) {
  const roomId = selectedRoomId ?? pickRoomId(roomSuggestion);
  const isPublic = publicSitePricingSettings != null;

  if (isPublic) {
    const agentRoomDisplayedPrice =
      resolveAgentRoomDisplayedPriceFromFare(ourFareDetails) ??
      ldsOurTotalFareDisplayNis(ourFareDetails?.ourTotalFare);
    if (agentRoomDisplayedPrice == null || agentRoomDisplayedPrice <= 0) {
      return { status: "invalid_agent_price", finalPriceIls: null };
    }
    const publicSnap = resolvePublicRoomPricingSnapshot({
      selectedRoomId: roomId,
      room: { ...(roomSuggestion || {}), ourFareDetails },
      agentRoomFinalPrice: agentRoomDisplayedPrice,
      publicSitePricingSettings,
      productType: "vacation_package_room",
      packageId,
      hotelId,
      board,
      occupancySignature: occupancy
        ? `${occupancy.adults}a${occupancy.kids}c${occupancy.infants || 0}i`
        : null,
      currency: "ILS",
      context: {
        isPublicSiteActive: true,
        isAgentPro: true,
        hasAgentHotelPricing: Boolean(agentHotelPricing),
      },
    });
    if (publicSnap?.status !== "ready") return publicSnap;
    const displayFmt = formatRoomIlsUsdDisplay(publicSnap.publicFinalPrice, ilsToUsd);
    return mapPublicSnapshotToNormalized(publicSnap, displayFmt, {
      basePriceWithCommissionIls: readEngineBasePriceWithCommissionIls(ourFareDetails),
      dealSeq: pickDealSeq(roomSuggestion) ?? (roomId != null ? String(roomId) : null),
      occupancySignature: occupancySignature(occupancy),
      supplierGross: num(roomSuggestion?.totalFare) ?? num(ourFareDetails?.ourTotalFare?.netPrice),
      currency: "ILS",
      fxRate: num(ourFareDetails?.ourTotalFare?.fx_rate),
      via: "public_room_snapshot",
    });
  }

  const tf = ourFareDetails?.ourTotalFare;
  const feeCtx = buildFeeCtx({
    selectedResult,
    handleFeeData,
    numberOfRooms,
    getCreditCardFeePercent,
  });
  const displayIls = getFinalIlsFromOurTotalFareForDisplay(tf, feeCtx);
  if (displayIls == null || displayIls <= 0) {
    return { status: "invalid_agent_price", finalPriceIls: null };
  }
  const displayFmt = formatRoomIlsUsdDisplay(displayIls, ilsToUsd);
  return mapAgentSnapshotToNormalized({
    displayIls,
    ourFareDetails,
    displayFmt,
    roomSuggestion,
    occupancy,
    via: "agent_room_ourFareDetails_ssot",
  });
}

/**
 * SSOT resolver for add-room occupancy preview cards and manual add-room commit.
 */
export function resolveVpAddRoomOccupancyPrice({
  occupancy,
  extendedDealApiData,
  roomsAndPaxInfo = [],
  selectedResult = null,
  calculateLDSDealFare,
  handleFeeData = null,
  getCreditCardFeePercent = null,
  numberOfRooms = 1,
  publicSitePricingSettings = null,
  agentHotelPricing = null,
  ilsToUsd = null,
}) {
  if (!occupancy || !selectedResult || typeof calculateLDSDealFare !== "function") {
    return { price: 0, pricingSnapshot: null, via: "missing_input" };
  }

  const applyDealHrToAddRoomResult = (result) => result;

  const anchorSlot = Array.isArray(roomsAndPaxInfo) ? roomsAndPaxInfo[0] : null;
  const anchorOccupancy = resolveSlotOccupancy(anchorSlot);
  const occupancyType = normalizeRoomTypeLabel({
    roomType: occupancy.roomType ?? occupancy.RoomType,
  });
  const anchorType = normalizeRoomTypeLabel(anchorSlot?.room);
  const occupancySeq = pickDealSeq(occupancy);
  const anchorSeq = pickDealSeq(anchorSlot?.room);
  const identityMatchesAnchor =
    occupanciesMatch(anchorOccupancy, occupancy) &&
    (!occupancyType || !anchorType || occupancyType === anchorType) &&
    (!occupancySeq || !anchorSeq || occupancySeq === anchorSeq) &&
    occupancyType &&
    occupancyType === anchorType;

  if (identityMatchesAnchor) {
    const anchorSnap = readRoomPricingSnapshot(anchorSlot);
    if (anchorSnap) {
      return applyDealHrToAddRoomResult({
        price: num(anchorSnap.finalPriceIls) ?? 0,
        pricingSnapshot: anchorSnap,
        via: "anchor_slot_pricing_snapshot",
        ilsDisplayRounded: anchorSnap.ilsDisplayRounded ?? Math.round(anchorSnap.finalPriceIls),
        usdDisplayIntegerStr: anchorSnap.usdDisplayIntegerStr ?? null,
        usdRaw: anchorSnap.usdRaw ?? null,
        originalCurrency: anchorSnap.originalCurrency ?? null,
        finalPriceGBP: anchorSnap.finalPriceGBP ?? null,
        roomSuggestion: anchorSlot.room,
        ourFareDetails:
          anchorSlot.room?.ourFareDetails ??
          anchorSlot.room?.[ROOM_KEYS.OUR_FARE_DETAILS] ??
          null,
      });
    }
    const anchorRoomId = pickRoomId(anchorSlot?.room);
    const anchorFare = anchorSlot?.room?.ourFareDetails ?? anchorSlot?.room?.[ROOM_KEYS.OUR_FARE_DETAILS];
    if (anchorRoomId && anchorFare) {
      const snap = buildVacationPackageRoomPricingSnapshot({
        roomSuggestion: anchorSlot.room,
        ourFareDetails: anchorFare,
        occupancy,
        selectedRoomId: anchorRoomId,
        publicSitePricingSettings,
        agentHotelPricing,
        selectedResult,
        handleFeeData,
        getCreditCardFeePercent,
        numberOfRooms,
        ilsToUsd,
        board: selectedResult?.hotel?.board ?? null,
        packageId:
          selectedResult?.dealId ?? selectedResult?.fullDeal?.dealId ?? null,
        hotelId:
          selectedResult?.hotel?.hotelId ?? selectedResult?.hotel?.HotelId ?? null,
      });
      if (snap?.status === "ready" && num(snap.finalPriceIls) > 0) {
        return applyDealHrToAddRoomResult({
          price: snap.finalPriceIls,
          pricingSnapshot: snap,
          via: "anchor_slot_rebuilt_snapshot",
          ilsDisplayRounded: snap.ilsDisplayRounded,
          usdDisplayIntegerStr: snap.usdDisplayIntegerStr,
          usdRaw: snap.usdRaw,
          originalCurrency: snap.originalCurrency,
          finalPriceGBP: snap.finalPriceGBP,
          roomSuggestion: anchorSlot.room,
          ourFareDetails: anchorFare,
        });
      }
    }
  }

  const roomSuggestion = findExtendedDealRowForAddRoomOccupancy({
    occupancy,
    extendedDealApiData,
  });
  if (!roomSuggestion) {
    return { price: 0, pricingSnapshot: null, via: "no_matching_extended_row" };
  }

  const ourFareDetails = computeOurFareForSuggestion({
    roomSuggestion,
    occupancy,
    selectedResult,
    calculateLDSDealFare,
  });

  const snap = buildVacationPackageRoomPricingSnapshot({
    roomSuggestion,
    ourFareDetails,
    occupancy,
    selectedRoomId: pickRoomId(roomSuggestion),
    publicSitePricingSettings,
    agentHotelPricing,
    selectedResult,
    handleFeeData,
    getCreditCardFeePercent,
    numberOfRooms,
    ilsToUsd,
    board: selectedResult?.hotel?.board ?? null,
    packageId: selectedResult?.dealId ?? selectedResult?.fullDeal?.dealId ?? null,
    hotelId: selectedResult?.hotel?.hotelId ?? selectedResult?.hotel?.HotelId ?? null,
  });

  if (snap?.status !== "ready" || !(num(snap.finalPriceIls) > 0)) {
    return { price: 0, pricingSnapshot: snap, via: "snapshot_not_ready" };
  }

  return applyDealHrToAddRoomResult({
    price: snap.finalPriceIls,
    pricingSnapshot: snap,
    via: "extended_row_ssot",
    ilsDisplayRounded: snap.ilsDisplayRounded,
    usdDisplayIntegerStr: snap.usdDisplayIntegerStr,
    usdRaw: snap.usdRaw,
    originalCurrency: snap.originalCurrency,
    finalPriceGBP: snap.finalPriceGBP,
    roomSuggestion,
    ourFareDetails,
  });
}

/**
 * Resolve pricing snapshot for a slot — stored snapshot first, else rebuild from room fare.
 */
export function resolveSlotRoomPricingSnapshot(slot, slotIndex, ctx = {}) {
  const stored = readRoomPricingSnapshot(slot);
  if (stored) return stored;

  const room = slot?.room;
  const ourFareDetails = room?.ourFareDetails ?? room?.[ROOM_KEYS.OUR_FARE_DETAILS];
  const roomId = pickRoomId(room);
  if (!room || !ourFareDetails || !roomId) return null;

  const snap = buildVacationPackageRoomPricingSnapshot({
    roomSuggestion: room,
    ourFareDetails,
    occupancy: resolveSlotOccupancy(slot),
    selectedRoomId: roomId,
    ...ctx,
  });
  return snap?.status === "ready" ? snap : null;
}

/**
 * Total package rooms price = sum of per-room pricingSnapshot.finalPriceIls (SSOT).
 */
export function sumSelectedRoomsFinalPriceIls(roomsAndPaxInfo, ctx = {}) {
  if (!Array.isArray(roomsAndPaxInfo) || roomsAndPaxInfo.length === 0) return null;

  let sum = 0;
  let any = false;

  for (let i = 0; i < roomsAndPaxInfo.length; i++) {
    const slot = roomsAndPaxInfo[i];
    const snap = resolveSlotRoomPricingSnapshot(slot, i, ctx);
    const finalIls = num(snap?.finalPriceIls);
    if (finalIls != null && finalIls > 0) {
      sum += finalIls;
      any = true;
    }
  }

  return any ? round2(sum) : null;
}
